/**
 * Трассировка продажи в браузере (RP-SALE-TRACE): словарь точек и форма пакетов. Модуль универсальный — про кассовую службу он
 * ничего не знает: куда отправлять, решает приёмник (`TraceSink`), которого подставляет приложение.
 */

/**
 * Закрытый словарь этапов: его же знает сервер. Новый этап — правка обеих сторон, поэтому произвольной строки здесь нет.
 * Порядок — порядок продажи.
 */
export const TRACE_STAGES = [
  'click',
  'cart-queue-wait',
  'probe',
  'pay-request',
  'pay-response',
  'terminal-shown',
  'receipt-shown',
  'done',
  'retry',
] as const

export type TraceStage = (typeof TRACE_STAGES)[number]

/** Дополнительные сведения точки — только эти ключи; персональных данных в них нет по построению. */
export interface TraceMeta {
  /** Номер попытки (для `retry` и `pay-request`). */
  attempt?: number
  /** HTTP-статус ответа (для `pay-response`). */
  status?: number
  /** Вид оплаты или операции одним словом (`card`, `cash`). */
  kind?: string
  /** Сколько команд корзины ждало в очереди (для `cart-queue-wait`). */
  queued?: number
}

/** Контрольная точка на проводе. */
export interface TracePoint {
  stage: TraceStage
  /** Момент, ISO-8601 UTC. */
  at: string
  durationMs: number | null
  outcome: string | null
  meta: TraceMeta | null
}

/** Сеть кассира по `navigator.connection`; браузер без интерфейса даёт `null`. */
export interface TraceNetwork {
  effectiveType: string | null
  rttMs: number | null
  downlinkMbps: number | null
}

/** Вид события подробного режима. */
export type TraceDetailKind = 'http' | 'queue' | 'poll' | 'live'

/** Событие подробного режима. `route` — шаблон без идентификаторов и без параметров запроса. */
export interface TraceDetailEvent {
  kind: TraceDetailKind
  route: string
  method: string | null
  at: string
  durationMs: number | null
  status: number | null
  attempt: number | null
  bytes: number | null
}

/** Пакет точек одной продажи (один запрос; не больше {@link MAX_POINTS_PER_REQUEST}). */
export interface TracePointsBatch {
  /** Куда уходит пакет (в приложении — кабинет источника); для модуля это непрозрачная строка. */
  scope: string
  saleId: string
  attemptKey: string | null
  points: readonly TracePoint[]
  network: TraceNetwork | null
}

/** Пакет событий подробного режима (не больше {@link MAX_DETAIL_PER_REQUEST}). */
export interface TraceDetailBatch {
  scope: string
  saleId: string
  events: readonly TraceDetailEvent[]
}

/**
 * Чем кончилась отправка: `sent` — принято; `retry` — не дошло (сеть, неизвестный исход, 5xx), оставить в очереди; `drop` — сервер
 * отказал по существу (4xx: нет права, не по договору) — повтор бессмыслен.
 */
export type TraceDelivery = 'sent' | 'retry' | 'drop'

/** Приёмник пакетов. Реализует приложение через общий transport; сам он ничего не показывает. */
export interface TraceSink {
  sendPoints: (batch: TracePointsBatch) => Promise<TraceDelivery>
  sendDetail: (batch: TraceDetailBatch) => Promise<TraceDelivery>
}

/** Потолок точек в одном запросе — договор сервера. */
export const MAX_POINTS_PER_REQUEST = 64
/** Потолок событий подробного режима в одном запросе — договор сервера. */
export const MAX_DETAIL_PER_REQUEST = 200
